import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Code, Copy, CheckCircle, FileCode, Sparkles, Trash2, ArrowDown, Info, ArrowLeftRight, Download, History, X, Clock, HelpCircle } from 'lucide-react';
import { useLanguage } from '../i18n';

/**
 * Go Struct ↔ JSON 双向生成器组件
 * @param {Object} theme - 主题配置（绿色系）
 */
function GoStructToJson({ theme }) {
  const { t, language } = useLanguage();

  const [goCode, setGoCode] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('example'); // 'type' | 'example' | 'example-comment'
  const [direction, setDirection] = useState('go2json'); // 'go2json' | 'json2go'
  const [structMode, setStructMode] = useState('nested'); // 'nested' | 'inline' (仅用于 JSON→Go)
  const [copySuccess, setCopySuccess] = useState(false);
  const [stats, setStats] = useState({ structs: 0, fields: 0 });

  // 历史记录相关 state
  const [history, setHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('go-json-history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showHistory, setShowHistory] = useState(false);

  // 保存历史记录
  const saveToHistory = (input, output, dir) => {
    if (!input || !output) return;

    const newRecord = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      direction: dir,
      input: input.substring(0, 500), // 只保存前 500 字符
      output: output.substring(0, 500),
      inputPreview: input.substring(0, 100),
      outputPreview: output.substring(0, 100),
    };

    const newHistory = [newRecord, ...history].slice(0, 20); // 最多保存 20 条
    setHistory(newHistory);
    localStorage.setItem('go-json-history', JSON.stringify(newHistory));
  };

  // 恢复历史记录
  const restoreHistory = (record) => {
    setGoCode(record.input);
    setDirection(record.direction);
    setShowHistory(false);
  };

  // 删除单条历史
  const deleteHistoryItem = (id) => {
    const newHistory = history.filter(item => item.id !== id);
    setHistory(newHistory);
    localStorage.setItem('go-json-history', JSON.stringify(newHistory));
  };

  // 清空所有历史
  const clearAllHistory = () => {
    if (window.confirm(language === 'zh' ? '确定要清空所有历史记录吗？' : 'Clear all history?')) {
      setHistory([]);
      localStorage.removeItem('go-json-history');
    }
  };

  // 解析 Go struct（增强版：支持多结构体和依赖分析）
  const parseGoStruct = (code) => {
    if (!code.trim()) return null;

    try {
      const structs = {};
      const structRegex = /type\s+(\w+)\s+struct\s*\{([^}]+)\}/gs;
      let match;

      while ((match = structRegex.exec(code)) !== null) {
        const structName = match[1];
        const fields = match[2];
        structs[structName] = parseFields(fields, structName);
      }

      if (Object.keys(structs).length === 0) {
        throw new Error(language === 'zh' ? '未找到有效的 struct 定义' : 'No valid struct definition found');
      }

      // 分析依赖关系：找出哪些结构体被其他结构体引用
      const referencedTypes = new Set();

      for (const fields of Object.values(structs)) {
        for (const field of fields) {
          let fieldType = field.type;

          // 处理指针类型：*UserProfile → UserProfile
          if (fieldType.startsWith('*')) {
            fieldType = fieldType.substring(1);
          }

          // 处理数组类型：[]Tag → Tag
          if (fieldType.startsWith('[]')) {
            fieldType = fieldType.substring(2);
            // 再次检查是否是指针数组：[]*Tag → Tag
            if (fieldType.startsWith('*')) {
              fieldType = fieldType.substring(1);
            }
          }

          // 如果这个类型是已定义的结构体，标记为被引用
          if (structs[fieldType]) {
            referencedTypes.add(fieldType);
          }
        }
      }

      // 识别顶层结构体：未被引用的结构体
      const topLevelStructs = Object.keys(structs).filter(name => !referencedTypes.has(name));

      // 如果没有顶层结构体（说明存在循环引用或全都互相引用），则使用第一个
      const roots = topLevelStructs.length > 0 ? topLevelStructs : [Object.keys(structs)[0]];

      return { structs, roots };
    } catch (err) {
      throw err;
    }
  };

  const parseFields = (fieldsStr, structName) => {
    const fields = [];
    const lines = fieldsStr.split('\n').map(l => l.trim()).filter(l => l);

    for (const line of lines) {
      const fieldMatch = line.match(/^(\w+)\s+([\w\[\]\*\.]+)(?:\s+`json:"([^"]+)"`)?/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const fieldType = fieldMatch[2];
        const jsonTag = fieldMatch[3] || fieldName.toLowerCase();
        const [jsonName] = jsonTag.split(',');

        // 提取注释（// 后面的内容）
        const commentMatch = line.match(/\/\/\s*(.+)$/);
        const comment = commentMatch ? commentMatch[1].trim() : '';

        fields.push({ name: fieldName, type: fieldType, jsonName: jsonName, comment: comment });
      }
    }

    return fields;
  };

  // Mock 数据生成器 - 增强版
  const generateExampleValue = (type, fieldName = '') => {
    if (type.startsWith('*')) return generateExampleValue(type.substring(1), fieldName);
    if (type.startsWith('[]')) {
      const elementType = type.substring(2);
      return [generateExampleValue(elementType, fieldName)];
    }
    if (type.startsWith('map[')) return { "key": "value" };

    // 根据字段名智能生成真实数据
    const lowerFieldName = fieldName.toLowerCase();

    switch (type) {
      case 'string':
        // 姓名相关
        if (lowerFieldName.includes('name') || lowerFieldName.includes('username')) {
          const names = ['张伟', '李娜', '王强', '刘芳', '陈杰', '杨静', '赵明', '黄丽'];
          return names[Math.floor(Math.random() * names.length)];
        }
        // 邮箱
        if (lowerFieldName.includes('email') || lowerFieldName.includes('mail')) {
          const domains = ['example.com', 'test.com', 'demo.cn'];
          const usernames = ['user', 'admin', 'test', 'demo'];
          return `${usernames[Math.floor(Math.random() * usernames.length)]}${Math.floor(Math.random() * 1000)}@${domains[Math.floor(Math.random() * domains.length)]}`;
        }
        // 手机号
        if (lowerFieldName.includes('phone') || lowerFieldName.includes('mobile') || lowerFieldName.includes('tel')) {
          return `1${[3,5,7,8,9][Math.floor(Math.random() * 5)]}${Math.floor(Math.random() * 100000000).toString().padStart(9, '0')}`;
        }
        // 地址
        if (lowerFieldName.includes('address') || lowerFieldName.includes('addr')) {
          const addresses = ['北京市朝阳区建国路88号', '上海市浦东新区世纪大道1000号', '广州市天河区珠江新城', '深圳市南山区科技园'];
          return addresses[Math.floor(Math.random() * addresses.length)];
        }
        // 城市
        if (lowerFieldName.includes('city')) {
          const cities = ['北京', '上海', '广州', '深圳', '杭州', '成都', '武汉', '西安'];
          return cities[Math.floor(Math.random() * cities.length)];
        }
        // 公司
        if (lowerFieldName.includes('company') || lowerFieldName.includes('corp')) {
          const companies = ['科技有限公司', '互联网公司', '信息技术公司', '创新科技'];
          return companies[Math.floor(Math.random() * companies.length)];
        }
        // 标题/描述
        if (lowerFieldName.includes('title')) {
          return '示例标题';
        }
        if (lowerFieldName.includes('desc') || lowerFieldName.includes('content')) {
          return '这是一段示例描述文本';
        }
        // URL
        if (lowerFieldName.includes('url') || lowerFieldName.includes('link')) {
          return 'https://example.com/path';
        }
        // 头像
        if (lowerFieldName.includes('avatar') || lowerFieldName.includes('photo') || lowerFieldName.includes('image')) {
          return 'https://example.com/avatar.jpg';
        }
        // 默认
        return '示例文本';

      case 'int': case 'int8': case 'int16': case 'int32': case 'int64':
      case 'uint': case 'uint8': case 'uint16': case 'uint32': case 'uint64':
        // ID 相关
        if (lowerFieldName.includes('id')) {
          return Math.floor(Math.random() * 10000) + 1;
        }
        // 年龄
        if (lowerFieldName.includes('age')) {
          return Math.floor(Math.random() * 50) + 18;
        }
        // 数量/计数
        if (lowerFieldName.includes('count') || lowerFieldName.includes('num') || lowerFieldName.includes('quantity')) {
          return Math.floor(Math.random() * 100) + 1;
        }
        // 价格/金额 (分)
        if (lowerFieldName.includes('price') || lowerFieldName.includes('amount') || lowerFieldName.includes('fee')) {
          return Math.floor(Math.random() * 100000) + 1000;
        }
        return Math.floor(Math.random() * 1000) + 1;

      case 'float32': case 'float64':
        // 价格 (元)
        if (lowerFieldName.includes('price') || lowerFieldName.includes('amount')) {
          return parseFloat((Math.random() * 1000 + 10).toFixed(2));
        }
        // 评分
        if (lowerFieldName.includes('rate') || lowerFieldName.includes('score')) {
          return parseFloat((Math.random() * 5).toFixed(1));
        }
        return parseFloat((Math.random() * 100).toFixed(2));

      case 'bool':
        // 状态相关倾向于 true
        if (lowerFieldName.includes('is') || lowerFieldName.includes('has') || lowerFieldName.includes('enable')) {
          return Math.random() > 0.3;
        }
        return true;

      case 'interface{}':
        return "any";

      default:
        return null;
    }
  };

  const generateTypeValue = (type) => {
    if (type.startsWith('*')) return generateTypeValue(type.substring(1));
    if (type.startsWith('[]')) return [];
    if (type.startsWith('map[')) return {};

    switch (type) {
      case 'string': return '';
      case 'int': case 'int8': case 'int16': case 'int32': case 'int64':
      case 'uint': case 'uint8': case 'uint16': case 'uint32': case 'uint64':
        return 0;
      case 'float32': case 'float64': return 0.0;
      case 'bool': return false;
      case 'interface{}': return null;
      default: return null;
    }
  };

  // 辅助函数：从类型中提取基础结构体名称
  const getBaseStructName = (fieldType) => {
    let baseType = fieldType;
    // 处理切片：[]Type 或 []*Type
    if (baseType.startsWith('[]')) {
      baseType = baseType.substring(2);
    }
    // 处理指针：*Type
    if (baseType.startsWith('*')) {
      baseType = baseType.substring(1);
    }
    return baseType;
  };

  const generateJSON = (parsed, useExample = true) => {
    const { structs, roots } = parsed;

    const buildObject = (structName) => {
      const fields = structs[structName];
      if (!fields) return {};

      const obj = {};
      for (const field of fields) {
        let value;
        const baseType = getBaseStructName(field.type);

        if (structs[field.type]) {
          // 直接是结构体类型
          value = buildObject(field.type);
        } else if (field.type.startsWith('[]') && structs[baseType]) {
          // 切片类型：[]StructName 或 []*StructName
          value = [buildObject(baseType)];
        } else if (field.type.startsWith('*') && structs[baseType]) {
          // 指针类型：*StructName
          value = buildObject(baseType);
        } else {
          // 基本类型，传递字段名以生成智能 Mock 数据
          value = useExample ? generateExampleValue(field.type, field.jsonName) : generateTypeValue(field.type);
        }
        obj[field.jsonName] = value;
      }
      return obj;
    };

    // 为每个顶层结构体生成独立的 JSON 块
    const jsonBlocks = roots.map(rootName => {
      const json = buildObject(rootName);
      const jsonStr = JSON.stringify(json, null, 2);
      return `// ${rootName} 示例\n${jsonStr}`;
    });

    return jsonBlocks.join('\n\n');
  };

  // 生成 JSON5 格式（带注释）
  const generateJSON5WithComments = (parsed, useExample = true) => {
    const { structs, roots } = parsed;

    const buildJSON5 = (structName, indent = 1) => {
      const fields = structs[structName];
      if (!fields) return '{}';

      const indentStr = '  '.repeat(indent);
      const lines = [];

      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        let value;
        let valueStr;
        const baseType = getBaseStructName(field.type);

        if (structs[field.type]) {
          // 直接是结构体类型
          value = buildJSON5(field.type, indent + 1);
          valueStr = value;
        } else if (field.type.startsWith('[]') && structs[baseType]) {
          // 切片类型：[]StructName 或 []*StructName
          const nestedJSON = buildJSON5(baseType, indent + 1);
          valueStr = `[${nestedJSON}]`;
        } else if (field.type.startsWith('*') && structs[baseType]) {
          // 指针类型：*StructName
          value = buildJSON5(baseType, indent + 1);
          valueStr = value;
        } else {
          // 基本类型
          value = useExample ? generateExampleValue(field.type, field.jsonName) : generateTypeValue(field.type);
          valueStr = JSON.stringify(value);
        }

        const comma = i < fields.length - 1 ? ',' : '';
        const comment = field.comment ? ` // ${field.comment}` : '';
        lines.push(`${indentStr}"${field.jsonName}": ${valueStr}${comma}${comment}`);
      }

      return '{\n' + lines.join('\n') + '\n' + '  '.repeat(indent - 1) + '}';
    };

    // 为每个顶层结构体生成独立的 JSON5 块（带注释）
    const jsonBlocks = roots.map(rootName => {
      const json5Str = buildJSON5(rootName);
      return `// ${rootName} 示例\n${json5Str}`;
    });

    return jsonBlocks.join('\n\n');
  };

  // JSON → Go Struct 转换逻辑
  const generateGoStruct = (jsonStr, structName = 'Response', inline = false) => {
    try {
      const obj = JSON.parse(jsonStr);

      // 转换为 PascalCase
      const toPascalCase = (str) => {
        return str
          .replace(/[-_](.)/g, (_, c) => c.toUpperCase())
          .replace(/^(.)/, (_, c) => c.toUpperCase())
          .replace(/[^a-zA-Z0-9]/g, '');
      };

      const inferType = (value, key = '') => {
        if (value === null) return 'interface{}';

        const type = typeof value;

        // 智能推断特殊字段
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('time') || lowerKey.includes('date') || lowerKey.includes('at')) {
          if (type === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            return 'time.Time';
          }
        }

        switch (type) {
          case 'string': return 'string';
          case 'number':
            return Number.isInteger(value) ? 'int' : 'float64';
          case 'boolean': return 'bool';
          case 'object':
            if (Array.isArray(value)) {
              if (value.length === 0) return '[]interface{}';
              const elementType = inferType(value[0]);
              return `[]${elementType}`;
            }
            // 嵌套对象，需要生成新的结构体
            return toPascalCase(key);
          default:
            return 'interface{}';
        }
      };

      // 生成内联匿名结构体
      const generateInlineStruct = (obj, indent = 1) => {
        const indentStr = '    '.repeat(indent);
        const fields = [];

        for (const [key, value] of Object.entries(obj)) {
          const fieldName = toPascalCase(key);
          const jsonTag = key;

          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // 嵌套对象 - 生成匿名结构体
            const nestedFields = generateInlineStruct(value, indent + 1);
            fields.push(`${indentStr}${fieldName} struct {\n${nestedFields}\n${indentStr}} \`json:"${jsonTag}"\``);
          } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            // 数组元素是对象 - 生成匿名结构体数组
            const nestedFields = generateInlineStruct(value[0], indent + 1);
            fields.push(`${indentStr}${fieldName} []struct {\n${nestedFields}\n${indentStr}} \`json:"${jsonTag}"\``);
          } else {
            const fieldType = inferType(value, key);
            fields.push(`${indentStr}${fieldName} ${fieldType} \`json:"${jsonTag}"\``);
          }
        }

        return fields.join('\n');
      };

      // 生成嵌套独立结构体
      const generateNestedStructs = (obj, name) => {
        const structs = [];
        const fields = [];

        for (const [key, value] of Object.entries(obj)) {
          const fieldName = toPascalCase(key);
          const jsonTag = key;
          let fieldType = inferType(value, key);

          // 如果是嵌套对象，递归生成结构体
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const nestedStructName = toPascalCase(key);
            const nestedStructs = generateNestedStructs(value, nestedStructName);
            structs.push(...nestedStructs);
            fieldType = nestedStructName;
          } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            // 数组元素是对象
            const elementStructName = toPascalCase(key.replace(/s$/, '')); // 去掉复数
            const elementStructs = generateNestedStructs(value[0], elementStructName);
            structs.push(...elementStructs);
            fieldType = `[]${elementStructName}`;
          }

          fields.push(`    ${fieldName} ${fieldType} \`json:"${jsonTag}"\``);
        }

        const structDef = `type ${name} struct {\n${fields.join('\n')}\n}`;
        structs.unshift(structDef);

        return structs;
      };

      if (inline) {
        // 内联模式
        const fields = generateInlineStruct(obj);
        return `type ${structName} struct {\n${fields}\n}`;
      } else {
        // 嵌套独立结构体模式
        const structs = generateNestedStructs(obj, structName);
        return structs.join('\n\n');
      }
    } catch (err) {
      throw new Error(`JSON 解析失败: ${err.message}`);
    }
  };

  // 自动识别输入类型
  const detectInputType = (input) => {
    const trimmed = input.trim();

    if (!trimmed) return null;

    // 检测 Go struct 特征
    // 1. 包含 type...struct 关键字
    // 2. 包含 json tag
    if (/type\s+\w+\s+struct\s*\{/i.test(trimmed)) {
      return 'go';
    }

    // 检测 JSON 特征
    // 1. 以 { 或 [ 开头
    // 2. 可以成功解析为 JSON
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        JSON.parse(trimmed);
        return 'json';
      } catch {
        // JSON 格式可能不完整，但看起来像 JSON
        if (trimmed.includes('"') && trimmed.includes(':')) {
          return 'json';
        }
      }
    }

    return null;
  };

  // 检测到的输入类型
  const [detectedType, setDetectedType] = useState(null);

  // 处理输入变化
  useEffect(() => {
    if (!goCode.trim()) {
      setJsonOutput('');
      setError('');
      setStats({ structs: 0, fields: 0 });
      setDetectedType(null);
      return;
    }

    const timer = setTimeout(() => {
      // 自动识别输入类型
      const detected = detectInputType(goCode);
      setDetectedType(detected);
      let autoDirection = direction;

      if (detected === 'go' && direction !== 'go2json') {
        autoDirection = 'go2json';
        setDirection('go2json');
      } else if (detected === 'json' && direction !== 'json2go') {
        autoDirection = 'json2go';
        setDirection('json2go');
      }

      try {
        if (autoDirection === 'go2json') {
          // Go → JSON
          const parsed = parseGoStruct(goCode);
          if (parsed) {
            let jsonStr;

            if (mode === 'example-comment') {
              // 生成带注释的 JSON5 格式
              jsonStr = generateJSON5WithComments(parsed, true);
            } else {
              // 生成标准 JSON（已经包含注释和格式化）
              jsonStr = generateJSON(parsed, mode === 'example');
            }

            setJsonOutput(jsonStr);
            setError('');

            // 保存到历史记录
            saveToHistory(goCode, jsonStr, 'go2json');

            // 统计信息
            const structCount = Object.keys(parsed.structs).length;
            const fieldCount = Object.values(parsed.structs).reduce((sum, fields) => sum + fields.length, 0);
            setStats({ structs: structCount, fields: fieldCount });
          }
        } else {
          // JSON → Go
          const goStruct = generateGoStruct(goCode, 'Response', structMode === 'inline');
          setJsonOutput(goStruct);
          setError('');

          // 保存到历史记录
          saveToHistory(goCode, goStruct, 'json2go');

          // 统计信息
          const structCount = (goStruct.match(/type \w+ struct/g) || []).length;
          const fieldCount = (goStruct.match(/`json:"[^"]+"`/g) || []).length;
          setStats({ structs: structCount, fields: fieldCount });
        }
      } catch (err) {
        setError(err.message || '解析失败');
        setJsonOutput('');
        setStats({ structs: 0, fields: 0 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [goCode, mode, direction, structMode]);

  // 复制 JSON
  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonOutput).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // 复制 Go 代码
  const copyGoCode = () => {
    navigator.clipboard.writeText(goCode).then(() => {
      setCopySuccess('gocode');
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // 切换方向
  const handleSwitchDirection = () => {
    // 如果有输出，将输出内容放到输入
    if (jsonOutput) {
      setGoCode(jsonOutput);
      setJsonOutput('');
    }
    setDirection(prev => prev === 'go2json' ? 'json2go' : 'go2json');
    setError('');
    setStats({ structs: 0, fields: 0 });
  };

  // 清空（带确认）
  const handleClear = () => {
    if (goCode.trim()) {
      if (window.confirm('确定要清空所有内容吗？')) {
        setGoCode('');
        setJsonOutput('');
        setError('');
        setStats({ structs: 0, fields: 0 });
      }
    }
  };

  // 下载 JSON
  const handleDownloadJson = () => {
    if (!jsonOutput) return;

    const blob = new Blob([jsonOutput], { type: direction === 'go2json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = direction === 'go2json' ? 'output.json' : 'structs.go';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 加载示例（带确认）
  const loadExample = () => {
    const example = direction === 'go2json' ? exampleGoCode : exampleJsonCode;
    if (goCode.trim()) {
      if (window.confirm('当前有内容，确定要加载示例并覆盖吗？')) {
        setGoCode(example);
      }
    } else {
      setGoCode(example);
    }
  };

  // 示例代码
  const exampleGoCode = `type User struct {
    ID       int      \`json:"id"\`
    Name     string   \`json:"name"\`
    Email    string   \`json:"email"\`
    Age      int      \`json:"age"\`
    Tags     []string \`json:"tags"\`
    Profile  *Profile \`json:"profile"\`
}

type Profile struct {
    Bio      string \`json:"bio"\`
    Avatar   string \`json:"avatar"\`
    IsPublic bool   \`json:"is_public"\`
}`;

  const exampleJsonCode = `{
  "id": 1001,
  "name": "张三",
  "email": "zhangsan@example.com",
  "age": 28,
  "created_at": "2025-01-15T10:30:00Z",
  "tags": ["developer", "golang", "backend"],
  "profile": {
    "bio": "全栈开发工程师",
    "avatar": "https://example.com/avatar.jpg",
    "is_public": true
  }
}`;

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900 py-8">
      <div className="px-8">
      {/* 顶部工具栏 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl px-6 py-4 mb-6 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-3">
              <FileCode size={28} className="text-emerald-600 dark:text-emerald-400" />
              {language === 'zh' ? 'Go Struct ↔ JSON 双向生成器' : 'Go Struct ↔ JSON Generator'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
              <span>{direction === 'go2json'
                ? (language === 'zh' ? 'Go 结构体 → JSON 示例数据' : 'Go Struct → JSON Example')
                : (language === 'zh' ? 'JSON 数据 → Go 结构体定义' : 'JSON → Go Struct Definition')}</span>
              <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded-full font-medium">
                {language === 'zh' ? '自动识别' : 'Auto Detect'}
              </span>
            </p>
          </div>

          {/* 操作按钮组 */}
          <div className="flex items-center gap-2">
            {/* 帮助入口 */}
            <Link
              to="/help/go-struct"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
              title={language === 'zh' ? '使用指南' : 'Guide'}
            >
              <HelpCircle size={16} />
              <span className="hidden sm:inline">{language === 'zh' ? '使用指南' : 'Guide'}</span>
            </Link>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-all flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow relative"
              title={language === 'zh' ? '查看历史记录' : 'View History'}
            >
              <History size={16} />
              {language === 'zh' ? '历史' : 'History'}
              {history.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {history.length}
                </span>
              )}
            </button>
            <button
              onClick={handleSwitchDirection}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-all flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow"
              title={language === 'zh' ? '切换转换方向' : 'Switch Direction'}
            >
              <ArrowLeftRight size={16} />
              {language === 'zh' ? '切换' : 'Switch'}
            </button>
            <button
              onClick={loadExample}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all flex items-center gap-2 text-sm font-medium shadow-sm hover:shadow"
            >
              <Sparkles size={16} />
              {language === 'zh' ? '示例' : 'Example'}
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 border border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-all flex items-center gap-2 text-sm font-medium"
            >
              <Trash2 size={16} />
              {t('common.clear')}
            </button>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 rounded-lg text-red-700 dark:text-red-300 flex items-start gap-3">
            <Info size={20} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">{language === 'zh' ? '解析错误' : 'Parse Error'}</div>
              <div className="text-sm">{error}</div>
            </div>
          </div>
        )}

        {/* 使用提示 */}
        <div className="mt-4 bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 rounded-lg p-3">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-800 dark:text-emerald-300">
              {language === 'zh' ? (
                <>
                  <span><strong>✨ 智能识别：</strong>自动判断输入是 Go struct 还是 JSON，无需手动切换 | </span>
                  {direction === 'go2json' ? (
                    <span><strong>Go→JSON：</strong>支持多种类型 | 自动识别 json tag | 类型/示例模式切换</span>
                  ) : (
                    <span><strong>JSON→Go：</strong>智能类型推断 | 时间字段识别 | <strong>标准模式</strong>（独立结构体，企业推荐）/ <strong>内联模式</strong>（匿名结构体，快速原型）</span>
                  )}
                </>
              ) : (
                <>
                  <span><strong>✨ Smart Detection:</strong> Auto-detect Go struct or JSON input | </span>
                  {direction === 'go2json' ? (
                    <span><strong>Go→JSON:</strong> Multiple types | Auto json tag | Type/Example mode</span>
                  ) : (
                    <span><strong>JSON→Go:</strong> Type inference | Time field detection | <strong>Standard</strong> (separate structs) / <strong>Inline</strong> (anonymous)</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区：左右布局 */}
      <div className="flex h-[calc(100vh-280px)] min-h-[400px] gap-6">
        {/* 左侧：输入区域 */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          {/* 输入标题栏 */}
          <div className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-gray-900 dark:text-gray-100">
                <Code size={20} className="text-emerald-600 dark:text-emerald-400" />
                <div>
                  <div className="font-semibold">{language === 'zh' ? '智能输入区' : 'Smart Input'}</div>
                  {detectedType && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        detectedType === 'go'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {language === 'zh'
                          ? `✓ 已识别：${detectedType === 'go' ? 'Go Struct' : 'JSON'} → 将生成 ${detectedType === 'go' ? 'JSON' : 'Go Struct'}`
                          : `✓ Detected: ${detectedType === 'go' ? 'Go Struct' : 'JSON'} → Will generate ${detectedType === 'go' ? 'JSON' : 'Go Struct'}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {goCode && (
                  <button
                    onClick={copyGoCode}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-sm"
                  >
                    {copySuccess === 'gocode' ? (
                      <>
                        <CheckCircle size={14} />
                        {t('common.copied')}
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        {t('common.copy')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            {/* 统计信息 */}
            {goCode && stats.structs > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 px-3 py-1 rounded-full text-xs font-medium">
                  {language === 'zh' ? `${stats.structs} 个结构体` : `${stats.structs} struct(s)`}
                </span>
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 px-3 py-1 rounded-full text-xs font-medium">
                  {language === 'zh' ? `${stats.fields} 个字段` : `${stats.fields} field(s)`}
                </span>
              </div>
            )}
          </div>

          {/* 输入框 */}
          <div className="flex-1 p-6 overflow-hidden">
            <textarea
              value={goCode}
              onChange={(e) => setGoCode(e.target.value)}
              placeholder={language === 'zh'
                ? '粘贴 Go struct 或 JSON 数据，系统将自动识别...\n\n支持格式：\n• Go: type User struct { Name string `json:"name"` }\n• JSON: { "name": "张三", "age": 25 }'
                : 'Paste Go struct or JSON data, auto-detection enabled...\n\nSupported formats:\n• Go: type User struct { Name string `json:"name"` }\n• JSON: { "name": "John", "age": 25 }'}
              className="w-full h-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/50 dark:focus:ring-emerald-400/50 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm resize-none transition-shadow"
            />
          </div>
        </div>

        {/* 中间：转换指示器 */}
        <div className="flex flex-col items-center justify-center w-20">
          {goCode && !error ? (
            <div className="flex flex-col items-center gap-4">
              <div className={`text-4xl ${jsonOutput ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500 animate-pulse'}`}>
                {direction === 'go2json' ? '→' : '←'}
              </div>
              <div className="text-center">
                {jsonOutput ? (
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle size={24} className="text-emerald-600 dark:text-emerald-400" />
                    <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                      {language === 'zh' ? '完成' : 'Done'}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin">
                      <ArrowDown size={24} className="text-gray-400 dark:text-gray-500" />
                    </div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {language === 'zh' ? '转换中' : 'Converting'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-4xl text-gray-300 dark:text-gray-600">
              {direction === 'go2json' ? '→' : '←'}
            </div>
          )}
        </div>

        {/* 右侧：输出区域 */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          {/* 输出标题栏 */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-gray-900 dark:text-gray-100">
                <FileCode size={20} className="text-emerald-600 dark:text-emerald-400" />
                <div>
                  <span className="font-semibold">
                    {jsonOutput ? (
                      direction === 'go2json'
                        ? (language === 'zh' ? '生成的 JSON 数据' : 'Generated JSON')
                        : (language === 'zh' ? '生成的 Go Struct 定义' : 'Generated Go Struct')
                    ) : (
                      language === 'zh' ? '输出区域' : 'Output'
                    )}
                  </span>
                  {jsonOutput && (
                    <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                      {direction === 'go2json'
                        ? (language === 'zh' ? '已成功生成 JSON 示例数据' : 'Successfully generated JSON')
                        : (language === 'zh' ? '已成功生成 Go 结构体定义' : 'Successfully generated Go struct')}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* JSON 值模式切换（仅在 Go→JSON 时显示） */}
                {direction === 'go2json' && jsonOutput && (
                  <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <button
                      onClick={() => setMode('type')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        mode === 'type'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                      title={language === 'zh' ? '零值模式' : 'Zero-value mode'}
                    >
                      {language === 'zh' ? '类型' : 'Type'}
                    </button>
                    <button
                      onClick={() => setMode('example')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        mode === 'example'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                      title={language === 'zh' ? '示例数据模式' : 'Example data mode'}
                    >
                      {language === 'zh' ? '示例' : 'Example'}
                    </button>
                    <button
                      onClick={() => setMode('example-comment')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        mode === 'example-comment'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                      title={language === 'zh' ? '带注释的示例数据（JSON5 格式）' : 'Example with comments (JSON5)'}
                    >
                      💬 {language === 'zh' ? '注释' : 'Comments'}
                    </button>
                  </div>
                )}

                {/* 结构体嵌套模式切换（仅在 JSON→Go 时显示） */}
                {direction === 'json2go' && jsonOutput && (
                  <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <button
                      onClick={() => setStructMode('nested')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        structMode === 'nested'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                      title={language === 'zh' ? '独立嵌套结构体（企业级推荐）' : 'Separate structs (Enterprise)'}
                    >
                      🏢 {language === 'zh' ? '标准' : 'Standard'}
                    </button>
                    <button
                      onClick={() => setStructMode('inline')}
                      className={`px-3 py-1.5 text-xs font-medium transition-all ${
                        structMode === 'inline'
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                      title={language === 'zh' ? '内联匿名结构体（快速原型）' : 'Inline anonymous structs (Prototype)'}
                    >
                      ⚡ {language === 'zh' ? '内联' : 'Inline'}
                    </button>
                  </div>
                )}

                {jsonOutput && (
                  <>
                    <button
                      onClick={handleDownloadJson}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-sm"
                      title={direction === 'go2json'
                        ? (language === 'zh' ? '下载 JSON 文件' : 'Download JSON')
                        : (language === 'zh' ? '下载 Go 文件' : 'Download Go')}
                    >
                      <Download size={14} />
                    </button>

                    <button
                      onClick={copyToClipboard}
                      className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-sm"
                    >
                      {copySuccess ? (
                        <>
                          <CheckCircle size={14} />
                          {t('common.copied')}
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          {t('common.copy')}
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 输出内容 */}
          <div className="flex-1 p-6 overflow-hidden">
            {jsonOutput ? (
              <pre className="w-full h-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-200 font-mono text-sm overflow-auto font-semibold">
                {jsonOutput}
              </pre>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
                {goCode && !error
                  ? (language === 'zh' ? '正在生成...' : 'Generating...')
                  : (language === 'zh' ? '等待输入' : 'Waiting for input')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 历史记录侧边栏 */}
      {showHistory && (
        <>
          {/* 遮罩层 */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowHistory(false)}
          />

          {/* 侧边栏 */}
          <div className="fixed right-0 top-0 bottom-0 w-96 bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col">
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-gray-800 dark:to-gray-700">
              <div className="flex items-center gap-3">
                <History size={24} className="text-emerald-600 dark:text-emerald-400" />
                <div>
                  <h2 className="text-lg font-semibold text-blue-900 dark:text-gray-100">
                    {language === 'zh' ? '历史记录' : 'History'}
                  </h2>
                  <p className="text-xs text-blue-700 dark:text-gray-400">
                    {language === 'zh' ? `最近 ${history.length} 条转换记录` : `${history.length} recent record(s)`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-white/50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* 列表 */}
            <div className="flex-1 overflow-y-auto p-4">
              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-600">
                  <History size={48} className="mb-4 opacity-50" />
                  <p className="text-sm">{language === 'zh' ? '暂无历史记录' : 'No history yet'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((record) => {
                    const time = new Date(record.timestamp);
                    const now = new Date();
                    const diff = Math.floor((now - time) / 1000);

                    let timeText = '';
                    if (language === 'zh') {
                      if (diff < 60) timeText = '刚刚';
                      else if (diff < 3600) timeText = `${Math.floor(diff / 60)}分钟前`;
                      else if (diff < 86400) timeText = `${Math.floor(diff / 3600)}小时前`;
                      else timeText = `${Math.floor(diff / 86400)}天前`;
                    } else {
                      if (diff < 60) timeText = 'Just now';
                      else if (diff < 3600) timeText = `${Math.floor(diff / 60)}m ago`;
                      else if (diff < 86400) timeText = `${Math.floor(diff / 3600)}h ago`;
                      else timeText = `${Math.floor(diff / 86400)}d ago`;
                    }

                    return (
                      <div
                        key={record.id}
                        className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all group"
                      >
                        {/* 记录信息 */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              record.direction === 'go2json'
                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}>
                              {record.direction === 'go2json' ? 'Go → JSON' : 'JSON → Go'}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                              <Clock size={12} />
                              {timeText}
                            </span>
                          </div>
                          <button
                            onClick={() => deleteHistoryItem(record.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-all"
                            title={language === 'zh' ? '删除' : 'Delete'}
                          >
                            <Trash2 size={14} className="text-red-600 dark:text-red-400" />
                          </button>
                        </div>

                        {/* 预览内容 */}
                        <div className="space-y-2">
                          <div className="bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{language === 'zh' ? '输入：' : 'Input:'}</div>
                            <pre className="text-xs text-gray-700 dark:text-gray-300 font-mono line-clamp-2 whitespace-pre-wrap break-all">
                              {record.inputPreview}...
                            </pre>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded p-2 border border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{language === 'zh' ? '输出：' : 'Output:'}</div>
                            <pre className="text-xs text-gray-700 dark:text-gray-300 font-mono line-clamp-2 whitespace-pre-wrap break-all">
                              {record.outputPreview}...
                            </pre>
                          </div>
                        </div>

                        {/* 操作按钮 */}
                        <button
                          onClick={() => restoreHistory(record)}
                          className="mt-3 w-full px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                        >
                          <History size={14} />
                          {language === 'zh' ? '恢复此记录' : 'Restore'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 底部操作 */}
            {history.length > 0 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={clearAllHistory}
                  className="w-full px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Trash2 size={16} />
                  {language === 'zh' ? '清空所有历史' : 'Clear All History'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default GoStructToJson;
